import React from 'react';
import { colors, type, space, formatMoney } from '../tokens';

// GitHub-contribution-style giving heatmap.
// Takes an array of {date, amount} gifts and bins them into weekly cells.
const GivingHeatmap = ({
  gifts = [],
  weeks = 52,
  cellSize = 12,
  gap = 3,
}) => {
  // Build week buckets (oldest → newest)
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const dayOfWeek = today.getDay();
  today.setDate(today.getDate() - dayOfWeek);

  const buckets = [];
  for (let w = weeks - 1; w >= 0; w--) {
    const start = new Date(today);
    start.setDate(start.getDate() - w * 7);
    const end = new Date(start);
    end.setDate(end.getDate() + 6);
    buckets.push({ start, end, amount: 0, count: 0 });
  }

  gifts.forEach(g => {
    const d = new Date(g.date);
    if (isNaN(d)) return;
    const bucket = buckets.find(b => d >= b.start && d <= b.end);
    if (bucket) {
      bucket.amount += g.amount || 0;
      bucket.count += 1;
    }
  });

  const max = Math.max(1, ...buckets.map(b => b.amount));
  const shade = (amount) => {
    if (amount === 0) return colors.surfaceInk;
    const ratio = Math.min(1, amount / max);
    if (ratio < 0.25) return '#c4e3d0';
    if (ratio < 0.5) return '#7ec39a';
    if (ratio < 0.75) return '#3fa367';
    return '#2e844a';
  };

  return (
    <div>
      <div style={{
        display: 'grid',
        gridTemplateColumns: `repeat(${weeks}, ${cellSize}px)`,
        gap,
      }}>
        {buckets.map((b, i) => (
          <div
            key={i}
            title={
              b.count
                ? `${formatMoney(b.amount)} · ${b.count} gift${b.count === 1 ? '' : 's'} · week of ${b.start.toLocaleDateString()}`
                : `No gifts · week of ${b.start.toLocaleDateString()}`
            }
            style={{
              width: cellSize,
              height: cellSize,
              background: shade(b.amount),
              borderRadius: 2,
              border: b.amount === 0 ? `1px solid ${colors.border}` : 'none',
              cursor: 'pointer',
            }}
          />
        ))}
      </div>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginTop: space[2],
        ...type.micro,
        color: colors.textTertiary,
      }}>
        <span>Past 52 weeks</span>
        <div style={{ display: 'flex', alignItems: 'center', gap: space[1] }}>
          <span>Less</span>
          {[colors.surfaceInk, '#c4e3d0', '#7ec39a', '#3fa367', '#2e844a'].map(c => (
            <div key={c} style={{ width: cellSize, height: cellSize, background: c, borderRadius: 2 }} />
          ))}
          <span>More</span>
        </div>
      </div>
    </div>
  );
};

export default GivingHeatmap;
